import { ChangeDetectionStrategy, Component, Signal, computed, inject } from '@angular/core';
import { MarkdownComponent } from 'ngx-markdown';
import { FLOATING_PANEL_DATA } from '@shared/floating-panel/floating-panel-data.token';
import { HdContentLanguageService } from '../hd-content-language.service';
import { HdGateInfoService } from '../hd-gate-info.service';
import { HdRelationInfoService, HdRelationInfoTopic } from '../relation-info/hd-relation-info.service';

/** Paneel-data: het benoemde thema van de geopende composite chart. */
export interface HdCompositeThemeInfoData {
  readonly theme: string;
  /** Thema-key: '5'..'9' (aantal samen gedefinieerde centers) of 'none'. */
  readonly themeKey?: string;
}

/**
 * Gedockt/zwevend info-paneel bij het composiet-thema (Eigenschappen-rij).
 * Benoemde thema's ('5'..'9') tonen de boektekst (relatieboek, theme-<n>.md)
 * in de actieve contenttaal; zonder benoemd thema blijft de placeholder staan.
 */
@Component({
  selector: 'hd-composite-theme-info',
  standalone: true,
  imports: [MarkdownComponent],
  template: `
    <section class="hd-composite-theme-info">
      <h3 class="hd-composite-theme-info__title">{{ theme() }}</h3>
      @if (markdownUrl(); as url) {
        <markdown class="hd-composite-theme-info__markdown" [src]="url" />
      } @else {
        <p class="hd-composite-theme-info__todo">{{ todoText() }}</p>
      }
    </section>
  `,
  styleUrl: './hd-composite-theme-info.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdCompositeThemeInfoComponent {
  private readonly panelData = inject(FLOATING_PANEL_DATA, { optional: true }) as
    Signal<HdCompositeThemeInfoData | null> | null;
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly relationInfo = inject(HdRelationInfoService);

  protected readonly theme = computed(() => this.panelData?.()?.theme ?? '');

  /** Boektekst-document van het benoemde thema; null bij 'none'/onbekend. */
  protected readonly markdownUrl = computed<string | null>(() => {
    const themeKey = this.panelData?.()?.themeKey;
    if (themeKey == null || !/^[5-9]$/.test(themeKey)) {
      return null;
    }
    return this.relationInfo.relationMarkdownUrl(`theme-${themeKey}` as HdRelationInfoTopic);
  });

  protected readonly todoText = computed(() => {
    this.contentLang.translationsLoadedTick();
    const lang = this.contentLang.effectiveLang();
    const value = this.gateInfo.cyclesCheatSheetTextIn('composite.themeInfoTodo', lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cyclesCheatSheetTextIn('composite.themeInfoTodo', 'nl');
  });
}
